import {
    Chip,
    EmailAPIFactory,
    EmptyContent,
    formatNumber,
    GenericWrapperContext,
    Header,
    Icon,
    LoadingContainer,
    MetricCard,
    TEmailReport
} from "@cakemail-org/ui-components-v2"
import { Grid, Stack } from "@mui/material"
import { useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { MetricCardsContainer } from "../../../../../components/contentTemplate/metricCardsContainer"
import { LastXDays } from "../../../../../components/lastXDays"
import { emptyReportLog } from "../../../../../data/emailAPI"
import { getComparedReportStat } from "../../../../../utils/components"
import { affixToPercent, returnEmailTagsQuery, returnProviderQuery } from "../../../../../utils/data"
import { getAPIDateRange } from "../../../../../utils/date"
import "../styles.scss"
import "./styles.scss"
import { TKeyEngagementMetrics } from "./types"


export function KeyEngagementMetrics({ timeRange, tagsFilter, providerFilter, controlled, controlledProps, filterOperator, refreshKey }: TKeyEngagementMetrics) {
    const genericWrapper = useContext(GenericWrapperContext)
    const { t } = useTranslation()
    const [stateMetrics, setStateMetrics] = useState<TEmailReport>()
    const [stateError, setStateError] = useState<boolean>(false)
    const [stateOldMetrics, setStateOldMetrics] = useState<TEmailReport>()
    const [stateLoading, setStateLoading] = useState<boolean>(true)

    let loading: boolean = false
    let metrics: TEmailReport | undefined = undefined
    let oldMetrics: TEmailReport | undefined = undefined
    let error: boolean = false

    // To aliviate multiple same component on a page, will minimize the ammount of api call. 
    // You cannot change the controlled property afterwards
    if (controlled) {
        metrics = controlledProps?.metrics
        oldMetrics = controlledProps?.oldMetrics
        loading = controlledProps?.loading || false
        error = controlledProps?.error || false
    } else {
        useEffect(fetchMetrics, [timeRange, tagsFilter, refreshKey])
        metrics = stateMetrics
        oldMetrics = stateOldMetrics
        loading = stateLoading
        error = stateError
    }

    function fetchMetrics() {
        setStateLoading(true)

        const apiTimeRange = getAPIDateRange({ range: timeRange })
        const p1 = EmailAPIFactory.getReport({
            tags: returnEmailTagsQuery(tagsFilter, { operator: filterOperator }),
            providers: returnProviderQuery({ providers: providerFilter, operator: filterOperator }),
            startTime: apiTimeRange.start,
            endTime: apiTimeRange.end
        }).then((data) => {
            setStateMetrics(data[0] || emptyReportLog)
            setStateError(false)
        }).catch(() => {
            setStateMetrics(undefined)
            setStateError(true)
        })

        const apiTimeRangeDouble = getAPIDateRange({ range: timeRange, rangeMultiplier: 2 })
        const p2 = EmailAPIFactory.getReport({
            tags: returnEmailTagsQuery(tagsFilter, { operator: filterOperator }),
            startTime: apiTimeRangeDouble.start,
            endTime: apiTimeRangeDouble.end,
        }).then((data) => {
            setStateOldMetrics(data[0] || emptyReportLog)
        })

        Promise.allSettled([p1, p2]).then((data) => {
            setStateLoading(false)
        })
    }

    const comparedSent = getComparedReportStat({ metrics: metrics, oldMetrics: oldMetrics, stat: "delivered" })
    const comparedOpen = getComparedReportStat({ metrics: metrics, oldMetrics: oldMetrics, stat: "unique_open_rate", isRate: true })
    const comparedClick = getComparedReportStat({ metrics: metrics, oldMetrics: oldMetrics, stat: "clickthru_rate", isRate: true })
    const comparedUnsub = getComparedReportStat({ metrics: metrics, oldMetrics: oldMetrics, stat: "unsubscribe_rate", isRate: true, reverse: true })
    const comparedBounce = getComparedReportStat({ metrics: metrics, oldMetrics: oldMetrics, stat: "bounce_rate", isRate: true, reverse: true })

    const apiTimeRange = getAPIDateRange({ range: timeRange })

    return <Stack className="analyticBlock keyEngagementMetrics">
        <Header
            heading={t("header-key-metrics-heading")}
            postHeading={<LastXDays apiTimeRange={apiTimeRange} addPreviousText={true} />}
            headingTooltip={t("heading-key-metrics-tooltip")}
        />

        <LoadingContainer loading={!metrics && !error} variant="linear">
            <MetricCardsContainer loading={loading}>
                {metrics &&
                    <>
                        <Grid item>
                            <MetricCard
                                heading={{
                                    iconName: "Sends24",
                                    name: t("general-emails-sent"),
                                    tooltip: t("general-emails-sent-tooltip")
                                }}
                                subHeading={{
                                    data: formatNumber(metrics.delivered, genericWrapper.user?.language),
                                    chipComponent: comparedSent && <Chip
                                        key="chip1"
                                        variant="outlined"
                                        color={comparedSent.color}
                                        icon={comparedSent.icon && <Icon name={comparedSent.icon} />}
                                        label={comparedSent.value}
                                    />
                                }}
                                metricRow={[
                                    { name: t("general-delivery_rate"), data: affixToPercent(metrics.delivery_rate, genericWrapper.user?.language) },
                                    { name: t("general-emails-submitted"), data: formatNumber(metrics.submitted, genericWrapper.user?.language) }
                                ]}
                            />
                        </Grid>
                        <Grid item>
                            <MetricCard
                                heading={{
                                    iconName: "Inbox24",
                                    name: t("general-unique_open_rate"),
                                    tooltip: t("general-unique_open_rate_tooltip")
                                }}
                                subHeading={{
                                    data: affixToPercent(metrics.unique_open_rate, genericWrapper.user?.language),
                                    chipComponent: comparedOpen && <Chip
                                        key="chip2"
                                        variant="outlined"
                                        color={comparedOpen.color}
                                        icon={comparedOpen.icon && <Icon name={comparedOpen.icon} />}
                                        label={comparedOpen.value}
                                    />
                                }}
                                metricRow={[
                                    { name: t("general-unique_open"), data: formatNumber(metrics.unique_open, genericWrapper.user?.language) },
                                    { name: t("general-open_rate"), data: affixToPercent(metrics.open_rate, genericWrapper.user?.language) },
                                    { name: t("general-total-opens"), data: formatNumber(metrics.open, genericWrapper.user?.language) },
                                ]}
                            />
                        </Grid>
                        <Grid item>
                            <MetricCard
                                heading={{
                                    iconName: "Clicks24",
                                    name: t("general-unique_click_rate"),
                                    tooltip: t("general-unique_click_rate-tooltip")
                                }}
                                subHeading={{
                                    data: affixToPercent(metrics.unique_click_rate, genericWrapper.user?.language),
                                    chipComponent: comparedClick && <Chip
                                        key="chip3"
                                        variant="outlined"
                                        color={comparedClick.color}
                                        icon={comparedClick.icon && <Icon name={comparedClick.icon} />}
                                        label={comparedClick.value}
                                    />
                                }}
                                metricRow={[
                                    { name: t("general-click_rate"), data: affixToPercent(metrics.click_rate, genericWrapper.user?.language) },
                                    { name: t("general-clickthru_rate"), data: affixToPercent(metrics.clickthru_rate, genericWrapper.user?.language) },
                                    { name: t("general-total-clicks"), data: formatNumber(metrics.click, genericWrapper.user?.language) },
                                ]}
                            />
                        </Grid>
                        <Grid item>
                            <MetricCard
                                heading={{
                                    iconName: "Unsubscribes24",
                                    name: t("general-unsubscribe_rate"),
                                    tooltip: t("general-unsubscribe_rate-tooltip")
                                }}
                                subHeading={{
                                    data: affixToPercent(metrics.unsubscribe_rate, genericWrapper.user?.language),
                                    chipComponent: comparedUnsub && <Chip
                                        key="chip4"
                                        variant="outlined"
                                        color={comparedUnsub.color}
                                        icon={comparedUnsub.icon && <Icon name={comparedUnsub.icon} />}
                                        label={comparedUnsub.value}
                                    />
                                }}
                                metricRow={[
                                    { name: t("general-unsubscribe_other"), data: formatNumber(metrics.unsubscribe, genericWrapper.user?.language) },
                                    { name: t("general-global-unsubscribes"), data: formatNumber(metrics.global_unsubscribe, genericWrapper.user?.language) },
                                    { name: t("general-flagged-as-spam"), data: formatNumber(metrics.spam, genericWrapper.user?.language) }
                                ]}
                            />
                        </Grid>
                        <Grid item>
                            <MetricCard
                                heading={{
                                    iconName: "Bounce24",
                                    name: t("general-bounce_rate"),
                                    tooltip: t("general-bounce-rate_tooltip")
                                }}
                                subHeading={{
                                    data: affixToPercent(metrics.bounce_rate, genericWrapper.user?.language),
                                    chipComponent: comparedBounce && <Chip
                                        key="chip5"
                                        variant="outlined"
                                        color={comparedBounce.color}
                                        icon={comparedBounce.icon && <Icon name={comparedBounce.icon} />}
                                        label={comparedBounce.value}
                                    />
                                }}
                                metricRow={[
                                    { name: t("general-total-bounces"), data: formatNumber(metrics.bounce, genericWrapper.user?.language) },
                                    { name: t("general-hard-bounced"), data: formatNumber(metrics.hard_bounce, genericWrapper.user?.language) },
                                    { name: t("general-soft-bounced"), data: formatNumber(metrics.soft_bounce, genericWrapper.user?.language) }

                                ]}
                            />
                        </Grid>
                    </>
                }

                {error && <EmptyContent
                    title={t("analytic-block-loading-error-title")}
                />}
            </MetricCardsContainer>
        </LoadingContainer>

    </Stack>
}

export default KeyEngagementMetrics
